import React, { FC, forwardRef, Ref, useEffect } from 'react';
import { AsteriskOutlinedIcon } from '@src/svg';
import { Controller } from 'react-hook-form';
import InputMask, { ReactInputMask } from 'react-input-mask';
import { ControlledTimeInputPropsType } from '@uikit/FormItems/constants';

import { getDynamicValidationColorsGlobal } from '@uikit/FormItems/ControlledItems/helpers';
import { toast } from 'react-toastify';

const TIME_PATTERN = /^\d{2}:\d{2}$/;

const isCompleteTime = (value?: string) => !!value && !value.includes('_');

const timeToMinutes = (value: string) => {
  if (!TIME_PATTERN.test(value)) {
    return null;
  }

  const [hours, minutes] = value.split(':').map(Number);

  if (hours > 23 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
};

export const ControlledTimeInput: FC<ControlledTimeInputPropsType> = forwardRef(
  (
    {
      name,
      title,
      IconLeft,
      IconRight,
      mask = '99:99:99',
      placeholder,
      onChange,
      onBlur,
      disabled,
      min,
      max,
      invalidFallback,
      control,
      rules,
      dataTestId,
    }: ControlledTimeInputPropsType,
    ref: Ref<ReactInputMask>
  ) => {
    const clampTimeValue = (value: string) => {
      if (!isCompleteTime(value)) return value;

      const valueMinutes = timeToMinutes(value);
      const minMinutes = min ? timeToMinutes(min) : null;
      const maxMinutes = max ? timeToMinutes(max) : null;

      if (valueMinutes === null) {
        return invalidFallback || max || min || value;
      }

      if (max && maxMinutes !== null && valueMinutes > maxMinutes) {
        return max;
      }

      if (min && minMinutes !== null && valueMinutes < minMinutes) {
        toast.error(`The value cannot be earlier than the ${min}`);
        return min;
      }

      return value;
    };

    return (
      <div className="input-text">
        <div className={!disabled ? '' : 'disabled'}>
          <label htmlFor={name} className="input-title-wrapper">
            <span className="input-title">
              {title}
              {!!rules?.required && (
                <span className="input-required-marker" aria-hidden="true">
                  <AsteriskOutlinedIcon
                    width={10}
                    height={10}
                    color="#fb6403"
                  />
                </span>
              )}
            </span>
          </label>

          <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => {
              const dynamicColor = getDynamicValidationColorsGlobal(
                field,
                !!fieldState.error
              );

              useEffect(() => {
                if (fieldState.error) {
                  const message = fieldState.error?.message;
                  message && toast.error(message);
                }
              }, [fieldState.error]);

              return (
                <span
                  className={`${fieldState.error ? 'input error' : 'input'} ${dynamicColor}`}
                >
                  {IconLeft && <span>{IconLeft}</span>}
                  <InputMask
                    data-test-id={dataTestId}
                    id={name}
                    mask={mask}
                    disabled={disabled}
                    placeholder={placeholder || 'HH:MM:SS'}
                    {...field}
                    ref={ref} // Forward the ref here
                    onChange={(e) => {
                      const clampedValue = clampTimeValue(e.target.value);
                      const nextEvent =
                        clampedValue === e.target.value
                          ? e
                          : {
                              ...e,
                              target: {
                                ...e.target,
                                value: clampedValue,
                              },
                            };

                      field.onChange(nextEvent);
                      if (onChange) onChange(nextEvent);
                    }}
                    onBlur={(e) => {
                      field.onBlur();
                      if (onBlur) onBlur(e);
                    }}
                  />
                  {IconRight && <span>{IconRight}</span>}
                </span>
              );
            }}
          />
        </div>
      </div>
    );
  }
);
